import React, { useState, useMemo, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, onSnapshot, getDocs, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge'; 

import { 
  Search, User, Wrench, X, ChevronRight, Briefcase, Star, AlertCircle, 
  LayoutDashboard, ShieldCheck, Zap, Loader2, ArrowRight, 
  MessageSquareWarning, ShieldAlert, LifeBuoy, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

import Navbar from '../components/ui/Navbar';
import logoImg from '../assets/logo-semfrase.png';
import planoFundo from "../assets/planodefundo.png"; 

// Função auxiliar para normalizar texto (remove acentos e deixa em minúsculo)
const normalizeText = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const ProfessionalCard = memo(({ tech, searchTerm, onContact, onViewProfile }) => {
  const displayName = tech.professionalData?.name || tech.name;
  const displayPhoto = tech.professionalData?.photoUrl || tech.photoURL;
  
  const reviewCount = tech.reviewCount || 0;
  const rating = tech.rating || 0;
  const isNew = reviewCount === 0;

  return (
    <div className="bg-white/95 p-6 rounded-[2.5rem] border border-slate-100 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 group hover:shadow-2xl transition-all transform-gpu animate-in fade-in zoom-in-95 duration-300">
      <div className="flex items-center gap-5 w-full md:w-auto">
        <div className="w-16 h-16 rounded-full border-2 border-orange-500/20 overflow-hidden shadow-sm bg-slate-50 flex items-center justify-center shrink-0">
           {displayPhoto ? (
             <img src={displayPhoto} alt={displayName} className="w-full h-full object-cover" />
           ) : (
             <User className="w-8 h-8 text-slate-300" />
           )}
        </div>
        <div className="text-left">
          <div className="flex items-center gap-2">
            <h4 className="font-black text-slate-900 text-lg uppercase italic leading-none">{displayName}</h4>
            <ShieldCheck size={14} className="text-blue-500" />
          </div>
          <p className="text-[10px] text-orange-600 font-black uppercase mt-1 tracking-widest flex items-center gap-1">
            <Zap size={10} className="fill-orange-600"/> {searchTerm || 'Especialista'}
          </p>
          
          <div className="mt-2">
            {isNew ? (
              <Badge className="bg-green-50 text-green-600 border-green-100 font-black uppercase italic text-[8px] px-2 py-0.5 rounded-lg shadow-sm">
                Novo Profissional
              </Badge>
            ) : (
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={11} className={`${i < Math.round(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
                  ))}
                  <span className="text-[10px] font-black text-slate-900 ml-1 italic">{rating.toFixed(1)}</span>
                </div>
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-tighter mt-0.5">{reviewCount} {reviewCount === 1 ? 'Avaliação' : 'Avaliações'}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-3 w-full md:w-auto">
        <Button 
          variant="ghost"
          className="flex-1 md:flex-none h-12 rounded-2xl font-black uppercase italic text-[9px] border-slate-100 bg-slate-50 hover:bg-slate-900 hover:text-white transition-all shadow-sm"
          onClick={() => onViewProfile(tech.id)}
        >
          VER PERFIL
        </Button>
        <Button 
          className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl px-8 h-12 text-[10px] uppercase shadow-lg shadow-blue-200 active:scale-95 transition-transform" 
          onClick={() => onContact(tech.id)}
        >
          CONTATAR
        </Button>
      </div>
    </div>
  );
});

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  
  const [dbFunctions, setDbFunctions] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingCount, setPendingCount] = useState(0); 
  const [hasSearched, setHasSearched] = useState(false);
  const [liveCity, setLiveCity] = useState("");
  const [liveProfile, setLiveProfile] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;
    const unsubUser = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLiveProfile(data);
        setLiveCity((data.addressDetails?.city || "").trim().toUpperCase());
      }
    });

    const unsubOrders = onSnapshot(query(collection(db, 'orders'), where('clientId', '==', user.uid)), (snapshot) => {
      const active = snapshot.docs.filter(doc => !['cancelled', 'completed', 'finalized'].includes(doc.data().status));
      setPendingCount(active.length);
    });

    return () => { unsubUser(); unsubOrders(); };
  }, [user]);

  // Filtro de Categorias (Ignora acentos)
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return dbFunctions;
    const normalizedSearch = normalizeText(searchTerm);
    return dbFunctions.filter(f => normalizeText(f.name).includes(normalizedSearch));
  }, [searchTerm, dbFunctions]);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchFunctions = async () => {
      try {
        const snap = await getDocs(collection(db, "functions"));
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(f => f.active !== false).sort((a,b) => a.name.localeCompare(b.name));
        setDbFunctions(data);
      } catch (e) { console.error(e); }
    };
    if (user) fetchFunctions();
  }, [user]);

  const handleSearchProfessional = useCallback(async (areaName) => {
    if (areaName.trim().length < 3) {
        return toast.error("Digite pelo menos 3 letras para pesquisar.");
    }

    if (!liveCity) return toast.error("Configure sua cidade no perfil.");
    setLoading(true);
    setSearchTerm(areaName);
    setHasSearched(true);
    
    const normalizedTargetArea = normalizeText(areaName);

    try {
      const q = query(
        collection(db, 'users'), 
        where('userType', '==', 'technician'), 
        where('technicianStatus', '==', 'approved')
      );
      
      const querySnapshot = await getDocs(q);
      const ordersSnap = await getDocs(collection(db, "orders"));
      const allEvaluations = ordersSnap.docs
        .map(d => d.data())
        .filter(o => o.rating && o.rating > 0);

      const filtered = querySnapshot.docs
        .map(doc => {
          const data = doc.data();
          const techId = doc.id;
          const techRatings = allEvaluations.filter(o => o.technicianId === techId);
          const totalRating = techRatings.reduce((acc, curr) => acc + curr.rating, 0);
          const avgRating = techRatings.length > 0 ? totalRating / techRatings.length : 0;

          return { 
            id: techId, 
            ...data,
            rating: avgRating,
            reviewCount: techRatings.length
          };
        })
        .filter(tech => {
          if (tech.id === user.uid) return false; 
          
          // Lógica de busca em áreas (Ignora acentos)
          const techAreas = tech.areas || tech.professionalData?.areas || [];
          const hasArea = techAreas.some(a => normalizeText(a).includes(normalizedTargetArea));

          const techCity = (tech.professionalData?.address?.city || tech.addressDetails?.city || "").trim().toUpperCase();
          return hasArea && techCity === liveCity;
        });

      setTechnicians(filtered);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { 
      console.error(e);
      toast.error('Erro na busca.'); 
    } finally { setLoading(false); }
  }, [liveCity, user]);

  const hasTechAccess = liveProfile?.userType === 'technician' || liveProfile?.userType === 'admin';

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-blue-600" /></div>;

  return (
    <div className="relative min-h-screen bg-slate-50/50 font-sans text-slate-900 overflow-x-hidden text-left">
      <div className="fixed inset-0 -z-10 opacity-15" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
      <Navbar />

      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 pt-28 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 text-left">
        <div className="lg:col-span-8 space-y-6 text-left">
          
          {liveProfile?.technicianStatus === 'rejected' && (
            <div className="bg-red-600 rounded-[2rem] p-8 shadow-2xl text-white relative overflow-hidden flex flex-col md:flex-row items-center gap-8 animate-in slide-in-from-top-6 duration-700">
               <ShieldAlert size={150} className="absolute right-[-30px] top-[-30px] opacity-10 rotate-12" />
               <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shrink-0 shadow-inner">
                  <MessageSquareWarning size={32} className="text-white" />
               </div>
               <div className="flex-1 text-center md:text-left z-10">
                 <h4 className="font-black uppercase italic text-2xl tracking-tighter leading-none mb-2">Inscrição Rejeitada</h4>
                 <div className="bg-black/20 p-5 rounded-2xl italic text-sm border border-white/10 shadow-inner">
                   <span className="font-black uppercase text-[10px] text-red-200 block mb-1">Motivo:</span>
                   "{liveProfile.rejectionReason || 'Dados inconsistentes.'}"
                 </div>
                 <Button onClick={() => navigate('/become-technician')} className="mt-6 bg-white text-red-600 hover:bg-slate-100 font-black px-10 h-14 rounded-2xl uppercase text-[11px] shadow-xl">REVISAR MEUS DADOS</Button>
               </div>
            </div>
          )}

          <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-8 md:p-10 rounded-[2.5rem] shadow-2xl text-white relative overflow-hidden">
              <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter leading-[0.9] italic mb-3">CONTRATE UM <br /> ESPECIALISTA</h2>
              <p className="text-blue-100 text-sm md:text-base font-bold italic opacity-90 uppercase">
                Atendimento em <span className="text-orange-400 underline underline-offset-4 decoration-2">{liveCity || '---'}</span>
              </p>
          </div>

          {hasSearched && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
               <div className="flex items-center justify-between px-2">
                  <h3 className="text-lg font-black text-slate-800 italic uppercase tracking-tighter flex items-center gap-2">
                    <div className="w-1.5 h-5 bg-orange-500 rounded-full shadow-lg"></div> Busca: <span className="text-blue-600 underline">{searchTerm}</span>
                  </h3>
                  <Button onClick={() => {setHasSearched(false); setSearchTerm('');}} className="h-10 px-5 rounded-full font-black uppercase text-[10px] bg-white text-slate-400 border border-slate-200 hover:text-red-600 shadow-sm flex items-center gap-2">
                    <X size={14} /> LIMPAR
                  </Button>
               </div>
               <div className="grid grid-cols-1 gap-4">
                  {loading ? (
                    <div className="col-span-full text-center py-20 font-black uppercase italic text-slate-400 animate-pulse">Sincronizando técnicos...</div>
                  ) : (
                    <>
                      {technicians.map((tech) => (
                        <ProfessionalCard key={tech.id} tech={tech} searchTerm={searchTerm} onContact={(id) => navigate(`/direct-order/${id}`, { state: { selectedArea: searchTerm } })} onViewProfile={(id) => navigate(`/profile/${id}`)} />
                      ))}
                      
                      <div className={`col-span-full bg-white/95 p-10 rounded-[2.5rem] text-center border-2 border-dashed border-slate-200 shadow-xl ${technicians.length > 0 ? 'mt-4' : ''}`}>
                          <h4 className="text-xl font-black uppercase italic text-slate-800 mb-2">{technicians.length > 0 ? "Precisa de mais opções?" : "Nenhum especialista online agora?"}</h4>
                          <Button onClick={() => navigate('/find-technician', { state: { selectedArea: searchTerm } })} className="bg-orange-500 hover:bg-orange-600 text-white font-black h-16 px-10 rounded-2xl shadow-xl uppercase text-xs flex items-center gap-3 mx-auto">
                            LANÇAR PEDIDO PÚBLICO <ArrowRight size={20}/>
                          </Button>
                      </div>
                    </>
                  )}
               </div>
            </div>
          )}

          <div className="bg-white/95 rounded-[2.5rem] shadow-xl border border-white overflow-hidden">
            <div className="p-6 border-b border-slate-50 bg-slate-50/50 flex items-center gap-3 font-black text-slate-400 text-[10px] uppercase tracking-widest">
               <Wrench className="w-4 h-4 text-blue-600" /> CATEGORIAS DISPONÍVEIS
            </div>
            <div className="max-h-[420px] overflow-y-auto custom-scrollbar p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredCategories.map((service) => (
                    <div key={service.id} onClick={() => handleSearchProfessional(service.name)} className="flex items-center justify-between p-5 bg-slate-50/50 rounded-2xl border border-slate-100 cursor-pointer hover:bg-blue-600 group transition-all duration-300 shadow-sm">
                        <span className="font-black text-slate-700 uppercase italic text-sm group-hover:text-white">{service.name}</span>
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-white group-hover:translate-x-1" />
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="relative group">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 group-focus-within:text-blue-600" />
            <Input 
              placeholder="PESQUISAR OUTRO SERVIÇO..." 
              className="h-16 md:h-20 pl-14 pr-16 text-base md:text-lg rounded-[2rem] border-none bg-white shadow-xl font-bold uppercase italic focus:ring-2 ring-blue-500/20" 
              value={searchTerm} 
              onChange={(e) => {setSearchTerm(e.target.value); setHasSearched(false);}} 
              onKeyDown={(e) => e.key === 'Enter' && searchTerm && handleSearchProfessional(searchTerm)}
            />
            {searchTerm && (
                <Button onClick={() => handleSearchProfessional(searchTerm)} className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-2xl bg-blue-600 text-white shadow-lg hover:bg-blue-700">
                    <ArrowRight size={20} />
                </Button>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6 text-left">
          <div className="bg-white/90 p-10 rounded-[3rem] border border-white shadow-2xl flex flex-col items-center text-center">
             <img src={logoImg} alt="Socorre Aí" className="w-36 mb-6" />
             <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] leading-tight">O ESPECIALISTA CERTO <br /> NO MOMENTO CERTO</p>
          </div>

          {pendingCount > 0 && (
            <div className="bg-orange-500 p-8 rounded-[3rem] border-4 border-white/20 flex flex-col items-center text-center shadow-2xl">
               <AlertCircle className="w-10 h-10 text-white mb-3" />
               <h3 className="text-xl font-black text-white uppercase italic leading-none mb-1">PENDÊNCIAS</h3>
               <p className="text-white font-bold mb-5 text-sm uppercase">Você tem <span className="text-2xl font-black underline">{pendingCount}</span> pedido ativo.</p>
               <Button onClick={() => navigate('/history')} className="bg-white text-orange-600 hover:bg-slate-100 font-black w-full h-12 rounded-2xl text-[10px] uppercase shadow-lg">VER HISTÓRICO</Button>
            </div>
          )}

          {hasTechAccess ? (
            <div className="bg-slate-900 p-8 rounded-[3rem] shadow-2xl border-t-4 border-orange-500 flex flex-col items-center text-center">
                <LayoutDashboard className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="text-xl font-black text-white uppercase italic mb-2">MODO TÉCNICO</h3>
                <Button onClick={() => navigate('/technician-dashboard')} className="w-full h-16 bg-white text-slate-900 hover:bg-orange-500 hover:text-white font-black rounded-2xl shadow-xl flex items-center justify-center gap-3 uppercase italic text-xs">PAINEL DE TRABALHO <ArrowRight size={18}/></Button>
            </div>
          ) : (
            <div className="bg-white p-8 rounded-[3rem] border-2 border-dashed border-blue-200 flex flex-col items-center text-center shadow-xl">
               <Briefcase className="w-12 h-12 text-orange-600 mb-4" />
               <h3 className="text-xl font-black text-slate-800 uppercase leading-none italic mb-2">QUER TRABALHAR?</h3>
               <Button onClick={() => navigate('/become-technician')} className="bg-orange-500 hover:bg-orange-600 text-white font-black w-full h-14 rounded-2xl shadow-xl uppercase text-[10px] tracking-widest">SER PARCEIRO</Button>
            </div>
          )}

          <div className="bg-blue-600 p-8 rounded-[3rem] shadow-2xl border-t-4 border-blue-400 flex flex-col items-center text-center">
              <LifeBuoy className="w-12 h-12 text-white mb-4 animate-spin-slow" />
              <h3 className="text-lg font-black text-white uppercase italic tracking-tighter mb-1">PRECISE DE AJUDA?</h3>
              <p className="text-[10px] font-bold text-blue-100 uppercase mb-6 italic opacity-80">Solicite suporte aqui!</p>
              <Button onClick={() => navigate('/support')} className="w-full h-14 bg-white text-blue-600 hover:bg-slate-100 font-black rounded-2xl shadow-xl flex items-center justify-center gap-3 uppercase italic text-[10px]">ABRIR CHAMADO <HelpCircle size={16}/></Button>
          </div>

          <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-2xl border border-white/10 relative overflow-hidden text-center">
             {[1,2,3,4,5].map(i => <Star key={i} size={14} className="fill-yellow-400 text-yellow-400 inline-block mx-0.5 mb-4" />)}
             <p className="text-[13px] font-black leading-tight italic uppercase tracking-tighter text-white/90">"Segurança e agilidade em um só lugar."</p>
             <div className="mt-4 flex items-center gap-2 text-[9px] font-black text-blue-400 uppercase tracking-widest justify-center">
                <ShieldCheck size={14} /> Sistema Verificado
             </div>
          </div>
        </div>
      </main>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }` }} />
    </div>
  );
}